import JSZip from 'jszip';
import { each, keys, omitBy, reduce, values } from 'lodash-es';
import { makeAutoObservable } from 'mobx';
import { UploadedFileWithPreview } from 'pages/uploadPage/uploadFiles/uploadFiles';

export type FolderNode = {
  name: string;
  folders: Record<string, FolderNode>;
  files: Record<string, File>;
};

export class FolderNodeModel {
  private _name: FolderNode['name'];
  private _files: FolderNode['files'];
  private _folders: Record<string, FolderNodeModel>;

  constructor(data: FolderNode) {
    this._files = data.files;
    this._name = data.name;
    this._folders = reduce(
      data.folders,
      (acc: Record<string, FolderNodeModel>, folder) => {
        acc[folder.name] = new FolderNodeModel(folder);
        return acc;
      },
      {}
    );
    makeAutoObservable(this);
  }

  public get name() {
    return this._name;
  }
  public get files() {
    return this._files;
  }
  public get folders() {
    return this._folders;
  }

  public deleteFile(name: string) {
    this._files = { ...omitBy(this._files, (file) => file.name === name) };
  }

  public deleteFolder(name: string) {
    this._folders = { ...omitBy(this._folders, (f) => f.name === name) };
  }

  public get allFiles() {
    return [
      ...values(this._files),
      ...reduce(
        this._folders,
        (acc: File[], folder) => {
          acc = [...acc, ...folder.allFiles];
          return acc;
        },
        []
      ),
    ];
  }

  public get allFolders() {
    return [
      ...values(this._folders),
      ...reduce(
        this._folders,
        (acc: FolderNodeModel[], folder) => {
          acc = [...acc, ...folder.allFolders];
          return acc;
        },
        []
      ),
    ];
  }

  public async getArchieve() {
    let result: UploadedFileWithPreview[] | Promise<UploadedFileWithPreview[]> =
      [];

    const rootFolderName = this._name;

    const zip = new JSZip();

    each(this.allFiles, (file) => {
      zip.file(file.webkitRelativePath, file);
    });

    result = new Promise<UploadedFileWithPreview[]>((resolve) => {
      zip.generateAsync({ type: 'blob' }).then(function (content) {
        const newFile = new File([content], rootFolderName, {
          type: content.type,
        });
        resolve([
          {
            originalFile: newFile,
          },
        ]);
      });
    });

    return result;
  }

  public static constructTree(files: File[]) {
    const root: FolderNode = { name: 'root', folders: {}, files: {} };

    each(files, (file) => {
      const parts = file.webkitRelativePath.split('/');

      let currentNode = root;
      each(parts, (part, i) => {
        if (i === parts.length - 1) {
          currentNode.files[part] = file;
        } else {
          if (!currentNode.folders[part]) {
            currentNode.folders[part] = {
              name: part,
              folders: {},
              files: {},
            };
          }

          currentNode = currentNode.folders[part];
        }
      });
    });

    if (keys(root.folders).length === 0) {
      return null;
    }

    return new FolderNodeModel(root.folders[keys(root.folders)[0]]);
  }
}
